import * as THREE from 'three';
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineDashedMaterial,
  LineSegments,
  Vector3,
} from 'three';

import { renderOrders } from '../render-orders';

export class PickingLine extends LineSegments {
  constructor(
    color?: THREE.Color,
    type: 'dashed' | 'solid' = 'solid',
    renderOrder: number = renderOrders.pickers.pickingLine,
  ) {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3));

    super(
      geometry,
      type === 'dashed'
        ? new LineDashedMaterial({
            color: color ?? new Color(0, 1, 0),
            depthTest: false,
            depthWrite: false,
            transparent: true,
            scale: 1,
            dashSize: 0.2,
            gapSize: 0.4,
          })
        : new LineBasicMaterial({
            color: color ?? new Color(0, 1, 0),
            depthTest: false,
            depthWrite: false,
            transparent: true,
          }),
    );

    this.renderOrder = renderOrder;
  }

  updateVertex(index: number, point: Vector3) {
    this.geometry.attributes.position.setXYZ(index, point.x, point.y, point.z);

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeBoundingSphere();
    this.computeLineDistances();
    this.updateMatrix();
  }

  setNonIndexedVertices(vertices: Vector3[]) {
    this.geometry.setIndex(null);
    this.geometry.setAttribute(
      'position',
      new Float32BufferAttribute(vertices.map((x) => x.toArray()).flat(), 3),
    );
    this.geometry.computeBoundingSphere();
    this.computeLineDistances();
    this.updateMatrix();
  }

  /**
   * Sets the vertices as pairs of points, reusing the buffer when the number of vertices is the same.
   * Use this instead of setNonIndexedVertices when the vertices are updated often, e.g. on mouse move.
   */
  setVertices(vertices: Vector3[]) {
    const position = this.geometry.getAttribute('position');

    if (this.geometry.getIndex() !== null || position.count !== vertices.length) {
      this.setNonIndexedVertices(vertices);
      return;
    }

    vertices.forEach((vertex, i) => position.setXYZ(i, vertex.x, vertex.y, vertex.z));
    position.needsUpdate = true;
    this.geometry.computeBoundingSphere();
    this.computeLineDistances();
  }

  setIndicesAndVertices(position: Float32BufferAttribute, indices?: number[]) {
    if (indices) {
      this.geometry.setIndex(indices);
    } else if (this.geometry.getIndex() !== null) {
      this.geometry = this.geometry.toNonIndexed();
    }

    this.geometry.setAttribute('position', position);

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeBoundingSphere();
    this.updateMatrix();
  }

  setColor(color: Color) {
    (this.material as LineBasicMaterial).color = color;
    (this.material as LineBasicMaterial).needsUpdate = true;
  }
}
